/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Button, Colors, Icon, Text } from '@zapier/design-system';
import {
  useAvroEvent,
  useDismissable,
  useRouter,
} from 'app/developer-v3/hooks';
import { getAppUrl } from 'app/developer-v3/utils/url/url';

const PERSIST_ID = 'emphasize-embedding-sidebar-toast';

const styleToast = css`
  padding: 10px 10px 20px 30px;
  box-shadow: 0 -1px 0 0 ${Colors.GrayWarm4};
  background: ${Colors.PrimeWhite};
  position: sticky;
  bottom: 0;
`;

const styleToastHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const styleTitle = css`
  display: flex;
  align-items: center;
`;

const styleCloseButton = css`
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  border-radius: 4px;

  &:focus {
    outline: 2px solid ${Colors.UiPrimaryStrongest};
  }
`;

const styleEmbedButton = css`
  margin-top: 8px;
  margin-right: 10px;
`;

interface EmbedToastProps {
  appId: string;
}

export function EmbedToast({ appId }: EmbedToastProps) {
  const { routeTo } = useRouter();
  const { dismiss, isVisible } = useDismissable({ persistId: PERSIST_ID });
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const appUrl = getAppUrl(appId);

  function handleDismiss() {
    dismiss();
    emitPartnerUserInteractionEvent({
      eventAction: 'click',
      interactionGoal: 'Close Boost user engagement toast',
      interactionName: 'boost_user_engagement_close_button_clicked',
    });
  }

  function handleEmbedToolsClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();

    emitPartnerUserInteractionEvent({
      eventAction: 'click',
      interactionGoal: 'Navigate user to Embed Zapier page',
      interactionName: 'view_embed_tools_button_clicked',
    });

    routeTo(`${appUrl}/embed`);
  }

  if (!isVisible) {
    return null;
  }

  return (
    <div css={styleToast}>
      <div css={styleToastHeader}>
        <div css={styleTitle}>
          <Icon color="UiPrimaryStrongest" name="miscBoltAlt" size={18} />

          <Text fontWeight={600} margin="0 0 0 10px" type="SmallPrint2">
            Boost user engagement
          </Text>
        </div>

        <button
          aria-label='Close "Boost user engagement" toast.'
          css={styleCloseButton}
          data-testid="closeButton"
          onClick={handleDismiss}
        >
          <Icon name="formX" size={18} />
        </button>
      </div>
      <Text>by bringing automation to your product</Text>
      <div css={styleEmbedButton}>
        <Button
          color="secondary"
          href={`${appUrl}/embed`}
          isFullWidth={true}
          onClick={handleEmbedToolsClick}
        >
          View embed tools
        </Button>
      </div>
    </div>
  );
}
